import React from 'react';

import { motion } from 'framer-motion';
import {
  ShieldCheck,
  MessageSquare,
  BookOpen,
  Settings,
  Calendar,
  PlusCircle,
  ExternalLink,
  ChevronRight,
  Award
} from 'lucide-react';
import { Link } from 'react-router-dom';
const ADMIN_MODULES = [
  {
    title: 'Customer Reviews Management',
    description: 'View genuine customer feedback and permanently delete verified database reviews. Direct moderation controls.',
    icon: MessageSquare,
    badge: 'Live & Active',
    badgeColor: 'bg-amber-100 text-amber-800 border-amber-200',
    link: '/admin/reviews',
    buttonText: 'Manage Reviews →',
    primary: true,
  },
  {
    title: 'Instructors & Classes Hub',
    description: 'Manage yoga and fitness classes, view enrolled students, organize schedules, and track active sessions.',
    icon: Calendar,
    badge: 'Portal',
    badgeColor: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    link: '/instructor-profile',
    buttonText: 'Open Dashboard →',
  },
  {
    title: 'Blog & Articles Management',
    description: 'Create, edit, and publish wellness blog posts, health articles, and community lifestyle guides.',
    icon: BookOpen,
    badge: 'Content',
    badgeColor: 'bg-blue-100 text-blue-800 border-blue-200',
    link: '/instructor/instructor-profile-blogs',
    buttonText: 'Manage Blogs →',
  },
  {
    title: 'Create New Blog Post',
    description: 'Draft and upload new wellness articles with rich media, headers, and SEO-ready thumbnails.',
    icon: PlusCircle,
    badge: 'Editor',
    badgeColor: 'bg-purple-100 text-purple-800 border-purple-200',
    link: '/instructor/instructor-profile-newblogs',
    buttonText: 'Write Article →',
  },
  {
    title: 'Account & Profile Settings',
    description: 'Configure partner information, credentials, biography, pricing, and social profile links.',
    icon: Settings,
    badge: 'System',
    badgeColor: 'bg-stone-100 text-stone-800 border-stone-200',
    link: '/instructor/instructor-profile-settings',
    buttonText: 'Configure Settings →',
  },
  {
    title: 'Live Website & Public Feed',
    description: 'Browse the live public MetaFit homepage, explore services, and verify real customer testimonial presentation.',
    icon: ExternalLink,
    badge: 'Frontend',
    badgeColor: 'bg-teal-100 text-teal-800 border-teal-200',
    link: '/#testimonials',
    buttonText: 'Visit Live Site →',
  },
];

const AdminDashboard: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#F5F0E3] text-[#15302B] font-inter p-4 sm:p-6 lg:p-10">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header Card */}
        <div className="bg-white/90 backdrop-blur-md border border-[#15302B]/10 rounded-3xl p-6 sm:p-8 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono tracking-widest text-[#2C4A3B] uppercase font-bold mb-2">
              <ShieldCheck className="w-5 h-5 text-[#E6A93F]" />
              <span>MetaFit Unified Admin Control Center</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-serif font-bold text-[#15302B]">
              Master Admin Panel
            </h1>
            <p className="text-sm sm:text-base text-[#3B5750] mt-1 max-w-2xl">
              Centralized administration portal for customer reviews, instructor management, class schedules, wellness blogs, and system settings.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/admin/reviews"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-[#2C4A3B] text-white text-sm font-semibold hover:bg-[#15302B] transition-colors shadow-sm"
            >
              <MessageSquare className="w-4 h-4 text-[#E6A93F]" />
              <span>Reviews Hub</span>
            </Link>
            <Link
              to="/instructor-profile"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-[#15302B] text-[#F7F1E5] text-sm font-semibold hover:bg-black transition-colors shadow-sm"
            >
              <Calendar className="w-4 h-4 text-[#E6A93F]" />
              <span>Classes Portal</span>
            </Link>
          </div>
        </div>

        {/* Admin Modules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ADMIN_MODULES.map((mod, idx) => {
            const Icon = mod.icon;
            return (
              <motion.div
                key={mod.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className={`group bg-white border ${
                  mod.primary ? 'border-[#2C4A3B]/30 ring-2 ring-[#2C4A3B]/10 shadow-md' : 'border-[#15302B]/10 shadow-sm'
                } rounded-3xl p-6 sm:p-7 flex flex-col justify-between hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-[#15302B]/5 flex items-center justify-center text-[#2C4A3B] group-hover:bg-[#2C4A3B] group-hover:text-white transition-colors">
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className={`text-[11px] font-semibold px-3 py-1 rounded-full border ${mod.badgeColor}`}>
                      {mod.badge}
                    </span>
                  </div>

                  <h3 className="text-xl font-serif font-bold text-[#15302B] mb-2 group-hover:text-[#2C4A3B] transition-colors">
                    {mod.title}
                  </h3>
                  <p className="text-sm text-[#3B5750] leading-relaxed mb-6">
                    {mod.description}
                  </p>
                </div>

                <div>
                  <Link
                    to={mod.link}
                    className={`inline-flex items-center justify-between w-full px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                      mod.primary
                        ? 'bg-[#2C4A3B] text-white hover:bg-[#15302B] shadow-sm'
                        : 'bg-[#FAF9F5] text-[#15302B] hover:bg-[#15302B] hover:text-white border border-[#15302B]/10'
                    }`}
                  >
                    <span>{mod.buttonText}</span>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Quick Reference Box */}
        <div className="bg-[#FAF9F5] border border-[#15302B]/10 rounded-3xl p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-4">
            <Award className="w-6 h-6 text-[#2C4A3B]" />
            <h2 className="text-lg font-serif font-bold text-[#15302B]">
              Quick Access Direct URLs
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs font-mono">
            <div className="bg-white p-3.5 rounded-xl border border-gray-200">
              <span className="text-gray-500 block mb-1">Reviews View & Delete</span>
              <strong className="text-[#2C4A3B]">/admin/reviews</strong>
            </div>
            <div className="bg-white p-3.5 rounded-xl border border-gray-200">
              <span className="text-gray-500 block mb-1">Classes & Schedule</span>
              <strong className="text-[#2C4A3B]">/instructor-profile</strong>
            </div>
            <div className="bg-white p-3.5 rounded-xl border border-gray-200">
              <span className="text-gray-500 block mb-1">Blog Management</span>
              <strong className="text-[#2C4A3B]">/instructor/instructor-profile-blogs</strong>
            </div>
            <div className="bg-white p-3.5 rounded-xl border border-gray-200">
              <span className="text-gray-500 block mb-1">Settings & Profile</span>
              <strong className="text-[#2C4A3B]">/instructor/instructor-profile-settings</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
